/* eslint-disable no-underscore-dangle */
import { createAction } from '@reduxjs/toolkit';
import type { CustomerState } from '../reducers/customer';
import { components } from '../schema/docs/api/admin';
import type { AppThunk } from '../store/configureStore';
import { throwOnError } from './utils';

type Customers = CustomerState['customers'];

type GetCustomerResponse = components['schemas']['Customer'];
type IndividualNumberIdentification = components['schemas']['IndividualNumberIdentification'];
type GetUserPrivateMoneysResponse = [components['schemas']['PrivateMoney']];

export const GET_CUSTOMERS = createAction<Customers>('GET_CUSTOMERS');
export const SET_CUSTOMERS_LOADING = createAction('SET_CUSTOMERS_LOADING');

export const getCustomers =
  (query = ''): AppThunk =>
  (dispatch) => {
    dispatch(SET_CUSTOMERS_LOADING());

    // the count value returned from /customers is incorrect
    // we need to call a seperate endpoint for it and merge the result together

    return Promise.all([
      fetch(`/api/customers?${query}`, {
        method: 'GET',
        credentials: 'same-origin',
      }).then<Customers>(throwOnError),
      fetch(`/api/customers/summary?${query}`).then(
        throwOnError<{
          count: number;
        }>
      ),
    ])
      .then(([customers, summery]) => ({
        ...customers,
        count: summery.count,
      }))
      .then((customers) => dispatch(GET_CUSTOMERS(customers)));
  };

// Fetchers

export const fetchCustomers = (query = '') =>
  fetch(`/api/customers?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError<Customers>);

export const getCustomer = (id: string, query = '') =>
  fetch(`/api/customers/${id}?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError<GetCustomerResponse>);

export const deleteCustomer = (id: string) =>
  fetch(`/api/customers/${id}`, {
    method: 'DELETE',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token }),
  }).then(async (response) => {
    if (!response.ok) {
      throw await response.json();
    }
    // won't return anything if successful since the response will be 204 No Content
  });

export const updateCustomer = (id: string, formData: Record<string, unknown> = {}) =>
  fetch(`/api/customers/${id}`, {
    method: 'PATCH',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...formData }),
  }).then(throwOnError);

export const getUserAttributes = (id: string, moneyId: string) => {
  return fetch(`/api/user-attributes/${moneyId}/${id}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError);
};

export const getUserAttributesSchema = (moneyId: string) =>
  fetch(`/api/user-attributes-schema/${moneyId}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError);

export const createUserAttributesSchema = (id: string, formData: Record<string, unknown> = {}) =>
  fetch(`/api/user-attributes-schema/${id}`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...formData }),
  }).then(throwOnError);

export const updateUserAttributesSchema = (id: string, formData: Record<string, unknown> = {}) =>
  fetch(`/api/user-attributes-schema/${id}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...formData }),
  }).then(throwOnError);

export const deleteUserAttributesSchema = (id: string) =>
  fetch(`/api/user-attributes-schema/${id}`, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token }),
  }).then((response) => {
    if (!response.ok) {
      throw response.json();
    }
  });

export const getUserPrivateMoneys = (id: string) =>
  fetch(`/api/customers/${id}/private-moneys`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError<GetUserPrivateMoneysResponse>);

export const grantIndividualNumberIdentification = (
  customerId: string,
  postData: { private_money_id: string; is_resident: boolean }
) =>
  fetch(`/api/customers/${customerId}/individual-number-identification`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
  }).then(throwOnError<IndividualNumberIdentification>);

export const revokeIndividualNumberIdentification = (
  customerId: string,
  privateMoneyId: string
) => {
  const query = new URLSearchParams({ private_money_id: privateMoneyId }).toString();

  return fetch(`/api/customers/${customerId}/individual-number-identification?${query}`, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token }),
  }).then(throwOnError<IndividualNumberIdentification>);
};
